<template>
  <header class="dn">
    <div class="dn-bar">
      <nuxt-link
        to="/dashboard"
        class="dn-logo"
        aria-label="Pedalsure — my policies"
      >
        <img
          src="~/assets/images/Pedalsure-Logo_Large.svg"
          alt="Pedalsure"
          width="1080"
          height="173"
        />
      </nuxt-link>

      <nav v-if="isAuthenticated" class="dn-primary" aria-label="Dashboard">
        <nuxt-link
          v-for="item in primaryLinks"
          :key="item.to"
          :to="item.to"
          class="dn-primary-link"
          :class="{ 'is-current': section === item.section }"
          :aria-current="section === item.section ? 'page' : null"
        >
          {{ item.title }}
        </nuxt-link>
      </nav>

      <div class="dn-end">
        <!-- Help -->
        <div ref="help" class="dn-menu dn-desktop">
          <button
            ref="helpButton"
            type="button"
            class="dn-trigger"
            :aria-expanded="openMenu === 'help' ? 'true' : 'false'"
            aria-controls="dn-help-menu"
            @click="toggle('help')"
          >
            Help
            <Caret :open="openMenu === 'help'" />
          </button>
          <div
            v-show="openMenu === 'help'"
            id="dn-help-menu"
            class="dn-panel dn-panel--help"
          >
            <p class="dn-panel-label">Talk to us</p>
            <button type="button" class="dn-item" @click="openChat">
              <NavIcon :paths="ICONS.chat" />
              <span class="dn-item-text">
                <span class="dn-item-title">Chat with support</span>
                <span class="dn-item-copy">Message our team</span>
              </span>
            </button>
            <a class="dn-item" href="tel:08008886745" @click="closeMenus">
              <NavIcon :paths="ICONS.phone" />
              <span class="dn-item-text">
                <span class="dn-item-title">0800 888 6745</span>
                <span class="dn-item-copy">Mon–Fri, 9am–5pm</span>
              </span>
            </a>
            <div class="dn-sep"></div>
            <nuxt-link
              v-for="link in helpLinks"
              :key="link.to"
              :to="link.to"
              class="dn-item dn-item--compact"
            >
              {{ link.title }}
            </nuxt-link>
          </div>
        </div>

        <!-- Account -->
        <div v-if="isAuthenticated" ref="account" class="dn-menu dn-desktop">
          <button
            ref="accountButton"
            type="button"
            class="dn-trigger dn-trigger--account"
            :aria-expanded="openMenu === 'account' ? 'true' : 'false'"
            aria-controls="dn-account-menu"
            @click="toggle('account')"
          >
            <span class="dn-avatar" aria-hidden="true">{{ initial }}</span>
            <span class="dn-account-name">{{ firstName || 'Account' }}</span>
            <Caret :open="openMenu === 'account'" />
          </button>
          <div
            v-show="openMenu === 'account'"
            id="dn-account-menu"
            class="dn-panel dn-panel--account"
          >
            <div class="dn-who">
              <span class="dn-avatar dn-avatar--lg" aria-hidden="true">
                {{ initial }}
              </span>
              <span class="dn-who-text">
                <span v-if="displayName" class="dn-who-name">
                  {{ displayName }}
                </span>
                <span class="dn-who-email">{{ userEmail }}</span>
              </span>
            </div>
            <nuxt-link to="/dashboard" class="dn-item">
              <NavIcon :paths="ICONS.policies" />
              <span class="dn-item-text">
                <span class="dn-item-title">My policies</span>
                <span class="dn-item-copy">Cover, bikes and documents</span>
              </span>
            </nuxt-link>
            <nuxt-link to="/dashboard/settings" class="dn-item">
              <NavIcon :paths="ICONS.settings" />
              <span class="dn-item-text">
                <span class="dn-item-title">Account settings</span>
                <span class="dn-item-copy">Your details and password</span>
              </span>
            </nuxt-link>
            <div class="dn-sep"></div>
            <p class="dn-panel-label">For customers</p>
            <nuxt-link to="/quote" class="dn-item">
              <NavIcon :paths="ICONS.bike" />
              <span class="dn-item-text">
                <span class="dn-item-title">Get a new quote</span>
                <span class="dn-item-copy"
                  >Cover a bike on a separate policy</span
                >
              </span>
            </nuxt-link>
            <nuxt-link to="/hiplok" class="dn-item">
              <NavIcon :paths="ICONS.lock" />
              <span class="dn-item-text">
                <span class="dn-item-title">Hiplok offer</span>
                <span class="dn-item-copy">Up to 40% off approved locks</span>
              </span>
            </nuxt-link>
            <div class="dn-sep"></div>
            <button
              type="button"
              class="dn-item dn-item--logout"
              @click="logout"
            >
              <NavIcon :paths="ICONS.logout" />
              Log out
            </button>
          </div>
        </div>

        <nuxt-link v-else to="/login" class="dn-login dn-desktop">
          Log in
        </nuxt-link>

        <!-- Phone / tablet -->
        <button
          type="button"
          class="dn-burger"
          :aria-expanded="isActive ? 'true' : 'false'"
          aria-controls="dn-drawer"
          aria-label="Open menu"
          @click="toggleMenu"
        >
          <span v-if="isAuthenticated" class="dn-avatar" aria-hidden="true">
            {{ initial }}
          </span>
          <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">
            <path
              d="M4 7h16M4 12h16M4 17h16"
              fill="none"
              stroke="currentColor"
              stroke-width="2"
              stroke-linecap="round"
            />
          </svg>
        </button>
      </div>
    </div>

    <div v-show="isActive" id="dn-drawer" class="dn-drawer">
      <div class="drawer">
        <div class="drawer-head">
          <nuxt-link to="/" class="drawer-logo" @click.native="closeMenu">
            <img
              src="~/assets/images/Pedalsure-Logo_Large.svg"
              alt="Pedalsure"
              width="1080"
              height="173"
            />
          </nuxt-link>
          <button
            class="drawer-close"
            aria-label="Close menu"
            @click="closeMenu"
          >
            <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">
              <path
                d="M6.5 6.5l11 11M17.5 6.5l-11 11"
                fill="none"
                stroke="currentColor"
                stroke-width="2"
                stroke-linecap="round"
              />
            </svg>
          </button>
        </div>

        <!-- Who am I signed in as — the question a logged-in menu should
             answer before anything else. -->
        <div v-if="isAuthenticated" class="drawer-account-card">
          <span class="drawer-avatar" aria-hidden="true">{{ initial }}</span>
          <span class="drawer-account-text">
            <span class="drawer-account-label">Signed in as</span>
            <span class="drawer-account-email">{{ userEmail }}</span>
          </span>
        </div>

        <ul class="drawer-links">
          <li v-for="link in drawerLinks" :key="link.to">
            <nuxt-link :to="link.to" @click.native="closeMenu">
              <span class="drawer-icon" aria-hidden="true">
                <svg viewBox="0 0 24 24" focusable="false">
                  <path
                    v-for="(d, i) in link.paths"
                    :key="i"
                    :d="d"
                    fill="none"
                    stroke="currentColor"
                    stroke-width="1.7"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                  />
                </svg>
              </span>
              <span class="drawer-link-text">
                <span class="drawer-link-title">{{ link.title }}</span>
                <span class="drawer-link-copy">{{ link.copy }}</span>
              </span>
              <svg
                class="drawer-chevron"
                viewBox="0 0 24 24"
                aria-hidden="true"
                focusable="false"
              >
                <path
                  d="M9.5 5.5l6.5 6.5-6.5 6.5"
                  fill="none"
                  stroke="currentColor"
                  stroke-width="2"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                />
              </svg>
            </nuxt-link>
          </li>
        </ul>

        <!-- A new quote is a separate policy, so no 50% here: that's only for
             a second bike added to an existing policy, offered on the policy
             page. A link, not the quick-quote modal, which this layout doesn't
             mount. -->
        <nuxt-link to="/quote" class="drawer-upsell" @click.native="closeMenu">
          <span class="drawer-upsell-icon" aria-hidden="true">+</span>
          <span>
            <b>Insuring a bike separately?</b>
            Get a new quote in a couple of minutes.
          </span>
        </nuxt-link>

        <div class="drawer-help">
          <p class="drawer-help-title">Need a hand?</p>
          <button class="drawer-help-row" @click="openChat">
            <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">
              <path
                d="M20 12a7.5 7.5 0 0 1-10.9 6.7L4.5 20l1.3-4.6A7.5 7.5 0 1 1 20 12z"
                fill="none"
                stroke="currentColor"
                stroke-width="1.7"
                stroke-linejoin="round"
              />
            </svg>
            Chat with support
          </button>
          <a class="drawer-help-row" href="tel:08008886745">
            <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">
              <path
                d="M6.5 4h3l1.5 3.7-2 1.4a11 11 0 0 0 5.9 5.9l1.4-2L20 14.5v3a2 2 0 0 1-2.2 2A15.5 15.5 0 0 1 4 6.2 2 2 0 0 1 6 4z"
                fill="none"
                stroke="currentColor"
                stroke-width="1.7"
                stroke-linejoin="round"
              />
            </svg>
            0800 888 6745
            <span class="drawer-help-note">Mon–Fri, 9am–5pm</span>
          </a>
        </div>

        <button
          v-if="isAuthenticated"
          class="button ps-button is-outlined is-inverted is-fullwidth drawer-logout"
          @click="logout"
        >
          Log out
        </button>
        <nuxt-link
          v-else
          to="/login"
          class="button ps-button is-outlined is-inverted is-fullwidth drawer-logout"
          @click.native="closeMenu"
        >
          Log in
        </nuxt-link>
      </div>
    </div>
  </header>
</template>

<script>
/* eslint-disable no-console */
import { auth } from '~/services/firebase'

// How long "Chat with support" waits for Zendesk before falling back to
// the contact page.
const CHAT_WAIT_MS = 8000

// 24px stroke icons for the menus.
const ICONS = {
  chat: ['M20 12a7.5 7.5 0 0 1-10.9 6.7L4.5 20l1.3-4.6A7.5 7.5 0 1 1 20 12z'],
  phone: [
    'M6.5 4h3l1.5 3.7-2 1.4a11 11 0 0 0 5.9 5.9l1.4-2L20 14.5v3a2 2 0 0 1-2.2 2A15.5 15.5 0 0 1 4 6.2 2 2 0 0 1 6 4z',
  ],
  policies: [
    'M6 3.5h8L18.5 8v12a.5.5 0 0 1-.5.5H6a.5.5 0 0 1-.5-.5V4a.5.5 0 0 1 .5-.5z',
    'M13.5 3.7V8.5h4.8',
    'M8.5 13h7M8.5 16.5h4.5',
  ],
  settings: [
    'M12 15a3 3 0 1 0 0-6 3 3 0 0 0 0 6z',
    'M19.4 15a1.6 1.6 0 0 0 .3 1.8l.1.1a2 2 0 1 1-2.8 2.8l-.1-.1a1.6 1.6 0 0 0-2.7 1.1v.2a2 2 0 1 1-4 0v-.1a1.6 1.6 0 0 0-2.8-1.1l-.1.1a2 2 0 1 1-2.8-2.8l.1-.1A1.6 1.6 0 0 0 3.5 14H3.3a2 2 0 1 1 0-4h.1A1.6 1.6 0 0 0 4.6 7.3l-.1-.1a2 2 0 1 1 2.8-2.8l.1.1a1.6 1.6 0 0 0 2.7-1.1V3.3a2 2 0 1 1 4 0v.1a1.6 1.6 0 0 0 2.8 1.1l.1-.1a2 2 0 1 1 2.8 2.8l-.1.1a1.6 1.6 0 0 0 1.1 2.7h.2a2 2 0 1 1 0 4h-.1a1.6 1.6 0 0 0-1.5 1z',
  ],
  bike: [
    'M5.5 19a3.5 3.5 0 1 0 0-7 3.5 3.5 0 0 0 0 7zM18.5 19a3.5 3.5 0 1 0 0-7 3.5 3.5 0 0 0 0 7z',
    'M5.5 15.5 9 6h4l3 6.5M9 6l2.5 9.5M16 6h3',
  ],
  lock: [
    'M5.5 10.5h13a1 1 0 0 1 1 1v7a1 1 0 0 1-1 1h-13a1 1 0 0 1-1-1v-7a1 1 0 0 1 1-1z',
    'M8 10.5V7.5a4 4 0 0 1 8 0v3',
  ],
  logout: ['M10 5H5.5v14H10', 'M14 8l4 4-4 4M18 12H9'],
}

const NavIcon = {
  functional: true,
  props: { paths: { type: Array, required: true } },
  render(h, { props }) {
    return h(
      'svg',
      {
        class: 'dn-icon',
        attrs: {
          viewBox: '0 0 24 24',
          'aria-hidden': 'true',
          focusable: 'false',
        },
      },
      props.paths.map((d) =>
        h('path', {
          attrs: {
            d,
            fill: 'none',
            stroke: 'currentColor',
            'stroke-width': 1.7,
            'stroke-linecap': 'round',
            'stroke-linejoin': 'round',
          },
        })
      )
    )
  },
}

const Caret = {
  functional: true,
  props: { open: Boolean },
  render(h, { props }) {
    return h(
      'svg',
      {
        class: ['dn-caret', { 'is-open': props.open }],
        attrs: {
          viewBox: '0 0 20 20',
          fill: 'currentColor',
          'aria-hidden': 'true',
        },
      },
      [
        h('path', {
          attrs: {
            d: 'M5.3 7.3a1 1 0 011.4 0L10 10.6l3.3-3.3a1 1 0 111.4 1.4l-4 4a1 1 0 01-1.4 0l-4-4a1 1 0 010-1.4z',
          },
        }),
      ]
    )
  },
}

export default {
  name: 'NavigationDashboard',
  components: { NavIcon, Caret },
  data() {
    return {
      ICONS,
      isActive: false,
      // 'help' | 'account' | null — which desktop menu is open.
      openMenu: null,
      primaryLinks: [
        { to: '/dashboard', title: 'My policies', section: 'policies' },
        { to: '/claim', title: 'Claims', section: 'claims' },
        {
          to: '/policy-information',
          title: 'What’s covered',
          section: 'covered',
        },
      ],
      helpLinks: [
        { to: '/faq', title: 'FAQs' },
        { to: '/claim', title: 'How claims work' },
        { to: '/contact-us', title: 'Contact us' },
      ],
      // A line of context under each label, so the menu says what's behind it.
      drawerLinks: [
        {
          to: '/dashboard',
          title: 'My policies',
          copy: 'Cover, bikes and documents',
          paths: [
            'M6 3.5h8L18.5 8v12a.5.5 0 0 1-.5.5H6a.5.5 0 0 1-.5-.5V4a.5.5 0 0 1 .5-.5z',
            'M13.5 3.7V8.5h4.8',
            'M8.5 13h7M8.5 16.5h4.5',
          ],
        },
        {
          to: '/claim',
          title: 'Make a claim',
          copy: 'Claims assessed within 24 hours',
          paths: [
            'M12 3.2l7 2.8v6c0 4.2-2.9 7.6-7 9-4.1-1.4-7-4.8-7-9V6l7-2.8z',
            'M9 12l2.2 2.2L15.2 10',
          ],
        },
        {
          to: '/dashboard/settings',
          title: 'Account settings',
          copy: 'Your details and password',
          paths: [
            'M12 15a3 3 0 1 0 0-6 3 3 0 0 0 0 6z',
            'M19.4 15a1.6 1.6 0 0 0 .3 1.8l.1.1a2 2 0 1 1-2.8 2.8l-.1-.1a1.6 1.6 0 0 0-2.7 1.1v.2a2 2 0 1 1-4 0v-.1a1.6 1.6 0 0 0-2.8-1.1l-.1.1a2 2 0 1 1-2.8-2.8l.1-.1A1.6 1.6 0 0 0 3.5 14H3.3a2 2 0 1 1 0-4h.1A1.6 1.6 0 0 0 4.6 7.3l-.1-.1a2 2 0 1 1 2.8-2.8l.1.1a1.6 1.6 0 0 0 2.7-1.1V3.3a2 2 0 1 1 4 0v.1a1.6 1.6 0 0 0 2.8 1.1l.1-.1a2 2 0 1 1 2.8 2.8l-.1.1a1.6 1.6 0 0 0 1.1 2.7h.2a2 2 0 1 1 0 4h-.1a1.6 1.6 0 0 0-1.5 1z',
          ],
        },
        {
          to: '/hiplok',
          title: 'Hiplok offer',
          copy: 'Up to 40% off approved locks',
          paths: [
            'M5.5 10.5h13a1 1 0 0 1 1 1v7a1 1 0 0 1-1 1h-13a1 1 0 0 1-1-1v-7a1 1 0 0 1 1-1z',
            'M8 10.5V7.5a4 4 0 0 1 8 0v3',
          ],
        },
        {
          to: '/policy-information',
          title: 'What’s covered',
          copy: 'Policy details, limits and exclusions',
          paths: [
            'M12 3.8a8.2 8.2 0 1 1 0 16.4 8.2 8.2 0 0 1 0-16.4z',
            'M12 11v5.2',
            'M12 7.8h.01',
          ],
        },
        {
          to: '/contact-us',
          title: 'Contact us',
          copy: 'Phone, email and opening hours',
          paths: [
            'M4.5 6h15a1 1 0 0 1 1 1v10a1 1 0 0 1-1 1h-15a1 1 0 0 1-1-1V7a1 1 0 0 1 1-1z',
            'M4 7l8 5.5L20 7',
          ],
        },
      ],
    }
  },
  computed: {
    isAuthenticated() {
      return this.$store.getters['user/isAuthenticated']
    },
    userEmail() {
      return this.$store.getters['user/userEmail']
    },
    displayName() {
      return (this.$store.getters['user/user']?.displayName || '').trim()
    },
    firstName() {
      return this.displayName.split(' ')[0]
    },
    initial() {
      return (this.displayName || this.userEmail || '?').charAt(0).toUpperCase()
    },
    // Which top-level area the current page belongs to, for the bar's
    // current-page underline. A claim started from a policy counts as Claims.
    section() {
      const path = this.$route.path
      if (/\/claim/.test(path)) return 'claims'
      if (path.startsWith('/dashboard')) return 'policies'
      if (path.startsWith('/policy-information')) return 'covered'
      return null
    },
  },
  watch: {
    $route() {
      this.closeMenu()
      this.closeMenus()
    },
  },
  mounted() {
    document.addEventListener('click', this.onDocumentClick)
    document.addEventListener('keydown', this.onKeydown)
  },
  beforeDestroy() {
    document.removeEventListener('click', this.onDocumentClick)
    document.removeEventListener('keydown', this.onKeydown)
    document.body.style.overflow = ''
  },
  methods: {
    toggle(menu) {
      this.openMenu = this.openMenu === menu ? null : menu
    },
    closeMenus() {
      this.openMenu = null
    },
    // A click anywhere outside the open menu closes it.
    onDocumentClick(event) {
      if (!this.openMenu) return
      const el = this.$refs[this.openMenu]
      if (el && !el.contains(event.target)) this.closeMenus()
    },
    // Escape closes whatever is open and puts focus back on its trigger.
    onKeydown(event) {
      if (event.key !== 'Escape') return
      if (this.openMenu) {
        const trigger = this.$refs[`${this.openMenu}Button`]
        this.closeMenus()
        if (trigger) trigger.focus()
      } else if (this.isActive) {
        this.closeMenu()
      }
    },
    toggleMenu() {
      this.isActive = !this.isActive
      // The drawer scrolls on its own; the page behind it shouldn't.
      document.body.style.overflow = this.isActive ? 'hidden' : ''
    },
    closeMenu() {
      this.isActive = false
      document.body.style.overflow = ''
    },
    switchTheme(color) {
      this.$store.dispatch('theme/setTheme', color)
    },
    // Zendesk is injected by GTM on the deferred `ps_deferred` event (see
    // plugins/deferred-third-party.js), so early in a page view zE may not
    // exist yet — a tap can even be the interaction that triggers the load.
    // Wait a few seconds for it rather than doing nothing, and send people to
    // the contact page if it never arrives (blocked, or not on this host:
    // the GTM/Zendesk setup doesn't load it on localhost).
    openChat() {
      this.closeMenu()
      this.closeMenus()
      const open = () => {
        zE('messenger', 'show')
        zE('messenger', 'open')
      }
      if (typeof zE === 'function') return open()
      const started = Date.now()
      const poll = setInterval(() => {
        if (typeof zE === 'function') {
          clearInterval(poll)
          open()
        } else if (Date.now() - started > CHAT_WAIT_MS) {
          clearInterval(poll)
          this.$router.push('/contact-us')
        }
      }, 250)
    },
    async logout() {
      try {
        this.closeMenu()
        this.closeMenus()
        await this.$store.dispatch('user/logout')
        this.$router.push({ path: '/', query: { ...this.$route.query } })
      } catch (err) {
        console.log('users/logout error', err)
      }
    },
    async currentUser() {
      const user = await auth.currentUser
      console.log(user)
    },
  },
}
</script>

<style lang="scss" scoped>
// --- Bar ----------------------------------------------------------------
.dn {
  position: sticky;
  top: 0;
  z-index: 40;
  // Solid, no backdrop-filter: a filter makes the header the containing
  // block for the fixed drawer inside it, clipping the drawer to the bar.
  background: $ps-white;
  border-bottom: 1px solid $ps-border-color;
}
.dn-bar {
  display: flex;
  align-items: center;
  gap: 28px;
  max-width: 1180px;
  height: 64px;
  margin: 0 auto;
  padding: 0 24px;

  @media (max-width: 640px) {
    height: 58px;
    padding: 0 16px;
  }
}

.dn-logo {
  flex: 0 0 auto;
  display: flex;
  align-items: center;

  img {
    display: block;
    width: 118px;
    height: auto;
  }
}

// --- Section links (desktop) ----------------------------------------------
.dn-primary {
  display: flex;
  align-self: stretch;
  gap: 4px;

  @media (max-width: 1023px) {
    display: none;
  }
}
.dn-primary-link {
  position: relative;
  display: flex;
  align-items: center;
  padding: 0 12px;
  font-size: 15px;
  font-weight: 500;
  color: $ps-body-color;
  text-decoration: none;

  &:hover {
    color: $ps-ink;
  }

  // The current section: ink text and an underline sitting on the bar's edge.
  &.is-current {
    font-weight: 600;
    color: $ps-ink;

    &::after {
      content: '';
      position: absolute;
      left: 12px;
      right: 12px;
      bottom: -1px;
      height: 2px;
      border-radius: 2px 2px 0 0;
      background: $ps-primary;
    }
  }
}

.dn-end {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-left: auto;
}
.dn-desktop {
  @media (max-width: 1023px) {
    display: none !important;
  }
}

// --- Menus --------------------------------------------------------------
.dn-menu {
  position: relative;
}
.dn-trigger {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  height: 40px;
  padding: 0 12px;
  border: 1px solid transparent;
  border-radius: $ps-radius-pill;
  background: none;
  font: inherit;
  font-size: 15px;
  font-weight: 500;
  color: $ps-body-color;
  cursor: pointer;

  &:hover,
  &[aria-expanded='true'] {
    background: $ps-surface;
    color: $ps-ink;
  }

  &--account {
    padding: 0 10px 0 4px;
    border-color: $ps-border-color;
    color: $ps-ink;
    font-weight: 600;
  }
}
.dn-account-name {
  max-width: 120px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
::v-deep .dn-caret {
  width: 16px;
  height: 16px;
  color: $ps-muted;
  transition: transform 0.15s;

  &.is-open {
    transform: rotate(180deg);
  }
}

.dn-avatar {
  flex: 0 0 auto;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 30px;
  height: 30px;
  border-radius: 50%;
  background: $ps-primary-50;
  color: $ps-primary;
  font-size: 14px;
  font-weight: 700;

  &--lg {
    width: 40px;
    height: 40px;
    font-size: 17px;
  }
}

.dn-panel {
  position: absolute;
  top: calc(100% + 10px);
  right: 0;
  z-index: 50;
  width: 300px;
  padding: 8px;
  border: 1px solid $ps-border-color;
  border-radius: $ps-radius-md;
  background: $ps-white;
  box-shadow: 0 16px 40px -12px rgba(20, 22, 31, 0.22),
    0 2px 6px rgba(20, 22, 31, 0.05);

  &--help {
    width: 280px;
  }
}
.dn-panel-label {
  padding: 8px 10px 4px;
  font-size: 11.5px;
  font-weight: 600;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: $ps-muted;
}
.dn-sep {
  height: 1px;
  margin: 6px 4px;
  background: $ps-divider;
}

.dn-item {
  display: flex;
  align-items: center;
  gap: 12px;
  width: 100%;
  min-height: 44px;
  padding: 8px 10px;
  border: 0;
  border-radius: $ps-radius-sm;
  background: none;
  font: inherit;
  font-size: 14.5px;
  font-weight: 500;
  color: $ps-ink;
  text-align: left;
  text-decoration: none;
  cursor: pointer;

  &:hover,
  &:focus-visible {
    background: $ps-surface;
    color: $ps-ink;
  }

  &--compact {
    min-height: 38px;
    color: $ps-body-color;
  }

  &--logout {
    color: $ps-body-color;

    &:hover {
      color: $ps-error;
    }
  }
}
::v-deep .dn-icon {
  flex: 0 0 auto;
  width: 20px;
  height: 20px;
  color: $ps-primary;

  .dn-item--logout & {
    color: currentColor;
  }
}
.dn-item-text {
  min-width: 0;
}
.dn-item-title {
  display: block;
  font-weight: 600;
}
.dn-item-copy {
  display: block;
  font-size: 12.5px;
  font-weight: 400;
  color: $ps-muted;
}

.dn-who {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 6px;
  padding: 10px 10px 12px;
  border-bottom: 1px solid $ps-divider;
}
.dn-who-text {
  min-width: 0;
}
.dn-who-name {
  display: block;
  font-size: 15px;
  font-weight: 700;
  color: $ps-ink;
}
.dn-who-email {
  display: block;
  overflow: hidden;
  font-size: 13px;
  color: $ps-muted;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.dn-login {
  display: inline-flex;
  align-items: center;
  height: 40px;
  padding: 0 16px;
  border: 1px solid $ps-primary-200;
  border-radius: $ps-radius-pill;
  font-size: 15px;
  font-weight: 600;
  color: $ps-primary;
  text-decoration: none;
}

// --- Phone / tablet -------------------------------------------------------
.dn-burger {
  display: none;
  align-items: center;
  gap: 8px;
  height: 42px;
  padding: 0 10px 0 6px;
  border: 1px solid $ps-border-color;
  border-radius: $ps-radius-pill;
  background: $ps-white;
  color: $ps-ink;
  cursor: pointer;

  svg {
    width: 22px;
    height: 22px;
  }

  @media (max-width: 1023px) {
    display: inline-flex;
  }
}

.dn-drawer {
  position: fixed;
  // Insets rather than 100vh: on mobile 100vh is the *large* viewport, so
  // the bottom of the panel hides behind the browser chrome.
  inset: 0;
  z-index: 2000;
  overflow-y: auto;
  overscroll-behavior: contain;
  -webkit-overflow-scrolling: touch;
  padding: 18px 20px 32px;
  background: $ps-primary;
  color: $white;

  @media (min-width: 1024px) {
    display: none !important;
  }
}

// --- Mobile drawer ------------------------------------------------------
.drawer-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 16px;
}
.drawer-logo img {
  display: block;
  width: 132px;
  height: auto;
  // The wordmark is dark ink; on indigo it has to read white.
  filter: brightness(0) invert(1);
}
.drawer-close {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  border: 1px solid rgba(255, 255, 255, 0.4);
  border-radius: 50%;
  background: transparent;
  color: $white;
  cursor: pointer;

  svg {
    width: 20px;
    height: 20px;
  }
}

.drawer-account-card {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 18px;
  padding: 12px 14px;
  border-radius: $ps-radius-md;
  background: rgba(255, 255, 255, 0.14);
}
.drawer-avatar {
  flex: 0 0 auto;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background: $white;
  font-size: 18px;
  font-weight: 700;
  color: $ps-primary;
}
.drawer-account-text {
  min-width: 0;
}
.drawer-account-label {
  display: block;
  font-size: $ps-text-label;
  color: rgba(255, 255, 255, 0.7);
}
.drawer-account-email {
  display: block;
  overflow: hidden;
  font-size: $ps-text-small;
  font-weight: 600;
  color: $white;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.drawer-links {
  li + li {
    border-top: 1px solid rgba(255, 255, 255, 0.18);
  }

  a {
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 10px 2px;
    color: $white;
  }

  // Current page reads as a filled tile — a colour change wouldn't show
  // against white-on-indigo.
  a.nuxt-link-exact-active {
    .drawer-link-title {
      font-weight: 700;
    }
    .drawer-icon {
      background: $white;
      color: $ps-primary;
    }
  }
}
.drawer-icon {
  flex: 0 0 auto;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 38px;
  height: 38px;
  border-radius: $ps-radius-md;
  background: rgba(255, 255, 255, 0.14);
  color: $white;

  svg {
    width: 20px;
    height: 20px;
  }
}
.drawer-link-text {
  flex: 1 1 auto;
  min-width: 0;
}
.drawer-link-title {
  display: block;
  font-size: 17px;
  font-weight: 600;
  line-height: 1.3;
}
.drawer-link-copy {
  display: block;
  margin-top: 1px;
  font-size: $ps-text-label;
  line-height: 1.35;
  color: rgba(255, 255, 255, 0.7);
}
.drawer-chevron {
  flex: 0 0 auto;
  width: 16px;
  height: 16px;
  color: rgba(255, 255, 255, 0.55);
}

.drawer-upsell {
  display: flex;
  align-items: center;
  gap: 12px;
  width: 100%;
  margin-top: 14px;
  padding: 12px 14px;
  border: 1px solid rgba(255, 255, 255, 0.35);
  border-radius: $ps-radius-md;
  background: transparent;
  font-size: 14px;
  line-height: 1.4;
  color: rgba(255, 255, 255, 0.8);
  text-align: left;
  text-decoration: none;
  cursor: pointer;

  &:hover {
    color: rgba(255, 255, 255, 0.8);
    border-color: $white;
  }

  b {
    color: $white;
  }
}
.drawer-upsell-icon {
  flex: 0 0 auto;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 34px;
  height: 34px;
  border-radius: $ps-radius-sm;
  background: $white;
  font-weight: 700;
  color: $ps-primary;
}

.drawer-help {
  margin-top: 14px;
  padding-top: 12px;
  border-top: 1px solid rgba(255, 255, 255, 0.18);
}
.drawer-help-title {
  margin-bottom: 6px;
  font-size: $ps-text-label;
  font-weight: 600;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  color: rgba(255, 255, 255, 0.6);
}
.drawer-help-row {
  display: flex;
  align-items: center;
  gap: 10px;
  width: 100%;
  padding: 8px 0;
  border: 0;
  background: transparent;
  font-size: $ps-text-small;
  color: $white;
  text-align: left;
  cursor: pointer;

  svg {
    flex: 0 0 auto;
    width: 19px;
    height: 19px;
  }
}
.drawer-help-note {
  margin-left: auto;
  font-size: $ps-text-label;
  color: rgba(255, 255, 255, 0.65);
}

.drawer-logout {
  margin-top: 14px;
  border-color: rgba(255, 255, 255, 0.6);
  background: transparent;
  color: $white;
}
</style>
